// RelationDisplayFields.js
//
// Helpers for Relation field `displayFields`: which target-type fields can be
// shown as muted metadata under the related record title.

import { formatPrice, resolveFieldCurrencyCode } from 'utilities/Currency';
import { getEnumOptionLabel } from 'utilities/EnumOptions';
import { normalizeSchemaFieldType, isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { formatLocationDisplayValue } from 'utilities/Location';
import { formatMeasurementValue } from 'utilities/Measurement';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getNodeSubtitle, isSubtitleFieldType } from 'utilities/NodeSubtitle';
import { formatPhoneValueForDisplay } from 'utilities/Phone';
import { extractRichTextPlainText } from 'utilities/RichText';
import { getUrlString } from 'utilities/UrlFieldValue';
import {
  formatTemporalFieldValue,
  isTemporalFieldType,
} from 'views/utils/temporalFieldFormat';

/** Field types that do not render cleanly as short muted metadata lines. */
const EXCLUDED_DISPLAY_FIELD_TYPES = new Set([
  'activity',
  'checklist',
  'color',
  'comments',
  'cover',
  'nodelist',
  'people',
  'qrcode',
  'relation',
  'signature',
]);

const RICHTEXT_MAX_CHARS = 160;

/**
 * Normalizes a Relation `displayFields` config to unique field name strings.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeRelationDisplayFields(value) {
  const list = Array.isArray(value) ? value : [];
  const seen = new Set();
  const next = [];

  list.forEach((entry) => {
    const key = typeof entry === 'string'
      ? entry.trim()
      : String(entry?.name || entry?.key || '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    next.push(key);
  });

  return next;
}

/**
 * Whether a target-type field may be offered as Relation display metadata.
 *
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isRelationDisplayFieldEligible(field) {
  if (!field || typeof field !== 'object') return false;
  const name = String(field.name || '').trim();
  if (!name || name === 'title') return false;
  const type = normalizeSchemaFieldType(field.type);
  if (!type || EXCLUDED_DISPLAY_FIELD_TYPES.has(type)) return false;
  return true;
}

/**
 * Builds CheckboxList options from a target type's schema fields.
 *
 * @param {unknown} fields
 * @returns {{ value: string, label: string }[]}
 */
export function getRelationDisplayFieldOptions(fields) {
  const list = Array.isArray(fields) ? fields : [];
  return list
    .filter((field) => isRelationDisplayFieldEligible(field))
    .map((field) => {
      const value = String(field.name || '').trim();
      return {
        value,
        label: String(field.label || '').trim() || value,
      };
    })
    .filter((option) => option.value);
}

/**
 * Resolves configured display field defs in target schema order.
 *
 * @param {unknown} displayFields
 * @param {unknown} targetFields
 * @returns {object[]}
 */
export function resolveRelationDisplayFieldDefs(displayFields, targetFields) {
  const requested = new Set(normalizeRelationDisplayFields(displayFields));
  if (!requested.size) return [];

  return (Array.isArray(targetFields) ? targetFields : []).filter((field) => {
    const name = String(field?.name || '').trim();
    return name
      && requested.has(name)
      && isRelationDisplayFieldEligible(field);
  });
}

/**
 * Reads a related node's value for a display field (root + data_* bags).
 *
 * @param {object|null|undefined} node
 * @param {object} field
 * @param {string} typeKey
 * @returns {unknown}
 */
function readRelationDisplayRawValue(node, field, typeKey) {
  const type = normalizeSchemaFieldType(field?.type);
  const name = String(field?.name || '').trim();

  if (isSubtitleFieldType(type) || name === 'subtitle') {
    return getNodeSubtitle(node);
  }
  if (type === 'tags' || name === 'tags') {
    return node?.tags;
  }

  return readNodeFieldRawValue(node, field, typeKey);
}

/**
 * Formats one display-field value as a single muted metadata line.
 *
 * @param {object|null|undefined} node
 * @param {object} field
 * @param {string} [typeKey]
 * @returns {string}
 */
export function formatRelationDisplayFieldValue(node, field, typeKey = '') {
  const type = normalizeSchemaFieldType(field?.type);
  const raw = readRelationDisplayRawValue(node, field, typeKey || node?.type || '');

  if (raw === undefined || raw === null || raw === '') return '';

  if (type === 'boolean') {
    return raw ? 'Yes' : 'No';
  }

  if (type === 'phone') {
    return formatPhoneValueForDisplay(raw) || '';
  }

  if (type === 'location') {
    return formatLocationDisplayValue(raw) || '';
  }

  if (type === 'money') {
    return formatPrice(raw, resolveFieldCurrencyCode(field)) || '';
  }

  if (type === 'currency') {
    return String(raw || '').trim().toUpperCase();
  }

  if (type === 'percentage') {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return '';
    return `${Math.max(0, Math.min(100, Math.round(numeric)))}%`;
  }

  if (type === 'measurement') {
    return formatMeasurementValue(raw) || '';
  }

  if (type === 'enum') {
    return getEnumOptionLabel(field?.options || [], raw, 'en') || String(raw);
  }

  if (type === 'multiselect') {
    const list = Array.isArray(raw) ? raw : [raw];
    return list
      .map((entry) => getEnumOptionLabel(field?.options || [], entry, 'en') || String(entry ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }

  if (type === 'tags') {
    return Array.isArray(raw)
      ? raw.map((tag) => String(tag || '').trim()).filter(Boolean).join(', ')
      : String(raw || '').trim();
  }

  if (type === 'url') {
    return getUrlString(raw) || '';
  }

  if (type === 'richtext') {
    const text = extractRichTextPlainText(raw).replace(/\s+/g, ' ').trim();
    if (!text) return '';
    if (text.length <= RICHTEXT_MAX_CHARS) return text;
    return `${text.slice(0, RICHTEXT_MAX_CHARS).trim()}…`;
  }

  if (isTemporalFieldType(type)) {
    return formatTemporalFieldValue({
      value: raw,
      fieldType: type,
      locale: 'en-US',
      dateOptions: {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      },
      dateTimeOptions: {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
    }) || '';
  }

  if (typeof raw === 'object') {
    return '';
  }

  return String(raw).trim();
}

/**
 * Builds muted metadata lines for a hydrated related node.
 *
 * @param {object|null|undefined} node
 * @param {unknown} displayFields
 * @param {unknown} targetFields
 * @param {string} [typeKey]
 * @returns {{ key: string, text: string }[]}
 */
export function getRelationDisplayLines(node, displayFields, targetFields, typeKey = '') {
  if (!node) return [];

  const resolvedTypeKey = String(typeKey || node?.type || '').trim();
  return resolveRelationDisplayFieldDefs(displayFields, targetFields)
    .map((field) => {
      const text = formatRelationDisplayFieldValue(node, field, resolvedTypeKey);
      if (!text) return null;
      return {
        key: String(field.name || '').trim(),
        text,
      };
    })
    .filter(Boolean);
}

export default {
  formatRelationDisplayFieldValue,
  getRelationDisplayFieldOptions,
  getRelationDisplayLines,
  isRelationDisplayFieldEligible,
  normalizeRelationDisplayFields,
  resolveRelationDisplayFieldDefs,
};
